import Sentry, { featureTags } from '@/services/sentry'
import { USER_PREFERENCE_TYPES, isValidUserPreferenceValue } from '@/business-logic/user-preferences/userPreferenceValues'

const PLAN_NAMES = ['essential', 'pro']
const isNonEmptyString = (value) => typeof value === 'string' && value.trim() !== ''
const isOptionalString = (value) => value === undefined || isNonEmptyString(value)
const isOptionalNumber = (value) => value === undefined || (typeof value === 'number' && Number.isFinite(value))

const findOptionsProblem = ({ options }) => {
  const optionsAreValid = Array.isArray(options) && options.length > 0
    && options.every((option) => option && typeof option.value === 'string' && isNonEmptyString(option.label))
  return optionsAreValid ? null : 'an enum needs options: [{ value: string, label: string }]'
}

const findNumberProblem = ({
  unit, min, max, step,
}) => {
  if (!isNonEmptyString(unit)) return 'a number needs a unit'
  return [min, max, step].every(isOptionalNumber) ? null : 'min, max and step must be numbers'
}

const findPlanProblem = ({ plan }) => {
  if (plan === undefined) return null
  const planIsValid = plan && PLAN_NAMES.includes(plan.required) && isNonEmptyString(plan.entitledGetter)
  return planIsValid ? null : `plan must be { required: ${PLAN_NAMES.join(' | ')}, entitledGetter: string }`
}

const findTypeSpecificProblem = (definition) => {
  if (definition.type === USER_PREFERENCE_TYPES.ENUM) return findOptionsProblem(definition)
  if (definition.type === USER_PREFERENCE_TYPES.NUMBER) return findNumberProblem(definition)
  return null
}

const findDefinitionProblem = (definition) => {
  if (!definition || typeof definition !== 'object') return 'the module does not export a setting object'
  if (!isNonEmptyString(definition.key)) return 'key must be a non-empty string'
  if (!Object.values(USER_PREFERENCE_TYPES).includes(definition.type)) {
    return `type must be one of ${Object.values(USER_PREFERENCE_TYPES).join(', ')}`
  }
  if (!isNonEmptyString(definition.label)) return 'label must be a non-empty string'
  const badOptionalField = ['description', 'group', 'featureFlag'].find((field) => !isOptionalString(definition[field]))
  if (badOptionalField) return `${badOptionalField} must be a non-empty string`
  if (definition.searchableBeforeRollout !== undefined && typeof definition.searchableBeforeRollout !== 'boolean') {
    return 'searchableBeforeRollout must be a boolean'
  }
  const problem = findTypeSpecificProblem(definition) || findPlanProblem(definition)
  if (problem) return problem
  const defaultIsValid = definition.default !== undefined && isValidUserPreferenceValue(definition, definition.default)
  return defaultIsValid ? null : "default is missing or fails the setting's own validation"
}

const reportRejectedSetting = (moduleName, problem) => {
  const message = `Skipped user preference module "${moduleName}": ${problem}`
  // eslint-disable-next-line no-console
  console.warn(message)
  Sentry.captureMessage(message, {
    level: 'warning',
    tags: featureTags({ featureName: 'advanced-settings', taskId: 'CORE-31' }),
  })
}

const moduleName = (path) => path.replace(/^\.\//, '')

export const PREFERENCE_KEYS_OWNED_ELSEWHERE = Object.freeze([
  'emailingFeatureUpdates',
  'emailingPromotions',
  'emailingUsefulContents',
  'groupParticipantsBy',
  'reportsHome',
  'reportsPins',
])

const findUserPreferenceModuleProblem = (definition) => {
  const problem = findDefinitionProblem(definition)
  if (problem) return problem
  return PREFERENCE_KEYS_OWNED_ELSEWHERE.includes(definition.key)
    ? `key "${definition.key}" belongs to an existing preference`
    : null
}

/**
 * Builds a frozen registry from a webpack `require.context` over a settings folder. Each module's default export
 * (or `module.exports`) is one setting. A module that fails to load, that `findProblem` rejects (it returns the
 * reason, or null), that sits in a `<key>/index.js` folder named differently from its key (its `ext` hooks are
 * found by folder name) or that reuses the key of an earlier module (modules are read in file-name order) is skipped
 * and passed to `reportRejected(moduleName, problem)`, never a crash.
 */
export const buildSettingRegistry = (settingsContext, { findProblem, reportRejected }) => {
  const seenKeys = new Set()
  const definitions = []
  settingsContext.keys().sort().forEach((path) => {
    let exported
    try {
      const loaded = settingsContext(path)
      exported = loaded && loaded.__esModule ? loaded.default : loaded
    } catch (error) {
      reportRejected(moduleName(path), `it failed to load (${error.message})`)
      return
    }
    const folder = /^\.\/([^/]+)\/index\.js$/.exec(path)
    const problem = findProblem(exported)
      || (folder && exported.key !== folder[1] ? `its folder must be named after its key "${exported.key}"` : null)
    if (problem) {
      reportRejected(moduleName(path), problem)
      return
    }
    if (seenKeys.has(exported.key)) {
      reportRejected(moduleName(path), `duplicate key "${exported.key}"`)
      return
    }
    seenKeys.add(exported.key)
    definitions.push(Object.freeze(exported))
  })
  return Object.freeze(definitions)
}

/**
 * Builds the preference registry from the `settings` folder. A module that fails validation, reuses an earlier
 * module's key or a key an existing preference owns, is skipped with a warning.
 */
export const buildUserPreferenceRegistry = (settingsContext) => buildSettingRegistry(settingsContext, {
  findProblem: findUserPreferenceModuleProblem,
  reportRejected: reportRejectedSetting,
})
